import React, { useState } from "react";
import { Tooltip } from "@vibe/core";
import { Add } from "@vibe/icons";
import { EXTERNAL_LINKS } from "@data/externalLinks";
import { useShareColumnContext } from "./ShareColumnContext";
import ShareColumnDialog from "./ShareColumnDialog";
import { requiresManualSetup } from "./shareColumnEligibility";

interface ShareColumnButtonProps {
  title: string;
  type: string;
  /** Shared rows offer the rest of the boards; unshared rows offer all of them. */
  tooltip?: string;
}

/**
 * The "+" beside a column name in the item view. Opens the board picker that creates the
 * column on other connected boards. Renders nothing outside a ShareColumnProvider or when the
 * item lives on a single board.
 */
const ShareColumnButton = ({
  title,
  type,
  tooltip = "Add this column to other boards",
}: ShareColumnButtonProps) => {
  const context = useShareColumnContext();
  const [open, setOpen] = useState(false);

  if (!context || context.linkedBoards.length < 2) return null;

  if (requiresManualSetup(type)) {
    return (
      <Tooltip content="Connected-board columns can't be created for you. Add a column with the same name on the other board and connect it to a board; it then syncs on its own.">
        <a
          href={EXTERNAL_LINKS.HOW_TO_USE}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs whitespace-nowrap rounded-full px-2 py-[1px] bg-[#FDF0D0] text-[#8A5A00] !no-underline"
        >
          Set up manually
        </a>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip content={tooltip}>
        <button
          type="button"
          aria-label={`Add ${title} to other boards`}
          onClick={() => setOpen(true)}
          className="flex justify-center items-center w-5 h-5 p-0 rounded-full border border-solid border-[#0073EA] text-[#0073EA] bg-transparent cursor-pointer shrink-0 transition-colors hover:bg-[#0073EA] hover:text-white focus-visible:bg-[#0073EA] focus-visible:text-white"
        >
          <Add size={14} />
        </button>
      </Tooltip>

      {open && <ShareColumnDialog identity={{ title, type }} onClose={() => setOpen(false)} />}
    </>
  );
};

export default ShareColumnButton;
